/**
 * TipTap-aware helpers for chapter variations.
 * Variation baseContent / content are stored as stringified TipTap JSON (B/I/U marks).
 * LLM rewrites remain plain prose; marks are transferred from the base manuscript.
 */

import {
  chapterContentToPlainText,
  chapterContentToRichParagraphs,
  serializeChapterContent,
} from "./chapterContent";
import { normalizeDialogueParagraphBreaks } from "./chapterVariationDialogue";
import {
  hydrateMarksFromSources,
  plainTextToRichParagraphs,
  richParagraphsToTipTapDoc,
  transferFormattingOntoRewrite,
  tryParseStringifiedTipTapDoc,
  looksLikeTipTapJsonString,
} from "./tipTapPlainText";

/** Plain text for diffs / validation — accepts TipTap JSON or legacy plain. */
export function variationContentToPlainText(value: unknown): string {
  return normalizeDialogueParagraphBreaks(chapterContentToPlainText(value));
}

/** True when value is already TipTap JSON (string or object). */
export function isTipTapVariationContent(value: unknown): boolean {
  if (value && typeof value === "object") {
    const rec = value as { type?: unknown };
    return rec.type === "doc";
  }
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (tryParseStringifiedTipTapDoc(trimmed)) return true;
  return looksLikeTipTapJsonString(trimmed);
}

/**
 * Snapshot chapter TipTap JSON for variation.baseContent.
 * Always returns canonical TipTap JSON string.
 */
export function snapshotVariationBaseContent(chapterContent: unknown): string {
  return serializeChapterContent(chapterContent);
}

/**
 * Build variation.content TipTap JSON from a plain (or TipTap) rewrite,
 * transferring bold / italic / underline from the base manuscript.
 */
export function buildVariationContentFromRewrite(
  baseChapterContent: unknown,
  rewrite: unknown
): string {
  const baseParas = chapterContentToRichParagraphs(baseChapterContent);
  const rewritePlain = normalizeDialogueParagraphBreaks(chapterContentToPlainText(rewrite));
  const rewriteParas = plainTextToRichParagraphs(rewritePlain);

  const transferred = transferFormattingOntoRewrite(baseParas, rewriteParas);
  return serializeChapterContent(richParagraphsToTipTapDoc(transferred));
}

/**
 * After compare-panel edits (plain rebuild), re-apply marks from the prior
 * TipTap variation (and base) onto the new prose.
 */
export function refreshVariationContentFormatting(
  previousVariationContent: unknown,
  baseContent: unknown,
  nextPlainOrTipTap: unknown
): string {
  const nextPlain = normalizeDialogueParagraphBreaks(chapterContentToPlainText(nextPlainOrTipTap));
  if (!nextPlain.trim()) {
    return serializeChapterContent("");
  }
  const nextParas = plainTextToRichParagraphs(nextPlain);
  const primary = chapterContentToRichParagraphs(previousVariationContent);
  const secondary = chapterContentToRichParagraphs(baseContent);
  const transferred = transferFormattingOntoRewrite(
    primary.length ? primary : secondary,
    nextParas
  );
  const hydrated = hydrateMarksFromSources(transferred, primary, secondary);
  return serializeChapterContent(richParagraphsToTipTapDoc(hydrated));
}

/**
 * Apply compare merge: merged plain text + marks from variation (preferred) and base.
 */
export function buildAppliedChapterContentFromMerge(
  mergedPlain: string,
  baseChapterContent: unknown,
  variationContent: unknown
): string {
  const plainParas = plainTextToRichParagraphs(normalizeDialogueParagraphBreaks(mergedPlain));
  const variationParas = chapterContentToRichParagraphs(variationContent);
  const baseParas = chapterContentToRichParagraphs(baseChapterContent);
  const hydrated = hydrateMarksFromSources(plainParas, variationParas, baseParas);
  const withSoftTransfer = hydrated.map((para, index) => {
    const hasMarks = para.runs.some((run) => run.bold || run.italic || run.underline);
    if (hasMarks) return para;
    const donor = variationParas[index] ?? baseParas[index];
    if (!donor?.runs?.length) return para;
    return {
      plain: para.plain,
      runs: transferFormattingOntoRewrite([donor], [para])[0]?.runs ?? para.runs,
    };
  });
  return serializeChapterContent(richParagraphsToTipTapDoc(withSoftTransfer));
}
